import { FC, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Flex,
	Form,
	Input,
	Modal,
	Row,
	Select,
	Space,
	Spin,
	Table,
	TableColumnType,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	InfoCircleFilled,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	BmppGroupRelationship,
	CustomerGroupMember,
	CustomerGroupNoteHistory,
	GetCustomerMemberResponse,
	MemberType,
	getCustomerGroupDetail,
	getCustomerMembers,
	updateCustomerGroup,
} from '@/libs/fleet-api/services/customer';
import { useApp } from '@/App';
import type { PageName } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import CustomerGroupAddMemberModal from './CustomerGroupAddMemberModal';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { uploader } from '@/libs/fleet-api/uploader';
import blobToBase64 from '@/utils/blob-to-base64';
import { formatCurrency, formatNPWPValue } from '@/libs/antd/formater';

type EditableMember = GetCustomerMemberResponse & {
	relationshipId: number | null;
	memberType: MemberType;
};

type FormValues = {
	groupName: string;
	groupAddress: string;
	startDate?: dayjs.Dayjs;
	notes?: string;
};

type EditCustomerGroupPageProps = {
	groupId: string;
	returnPage?: PageName;
};

const RELATIONSHIP_LABELS: Record<number, string> = {
	[BmppGroupRelationship.Parent]: 'Pihak Terkait',
	[BmppGroupRelationship.Subsidiary]: 'Bukan Pihak Terkait',
	[BmppGroupRelationship.Affiliate]: 'Kelompok Bukan Pihak Terkait',
};

const MEMBER_TYPE_SHORT: Record<string, string> = {
	estar: 'E',
	corporate: 'C',
	retail: 'R',
	local: 'L',
};

const MEMBER_TYPE_COLOR: Record<string, string> = {
	estar: '#1557BC',
	local: '#C59B04',
};

const EditCustomerGroupPage: FC<EditCustomerGroupPageProps> = ({ groupId, returnPage = 'FRMCustomerGroupListPage' }) => {
	const { navigate, message } = useApp();
	const { openCustomerDetail } = useModals();
	const queryClient = useQueryClient();
	const [form] = Form.useForm<FormValues>();

	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [editingMemberIndex, setEditingMemberIndex] = useState<number | null>(
		null,
	);
	const [editRelationshipId, setEditRelationshipId] = useState<
		number | null | undefined
	>();
	const [deletingMemberIndex, setDeletingMemberIndex] = useState<number | null>(
		null,
	);
	const [members, setMembers] = useState<EditableMember[]>([]);
	const [treeFile, setTreeFile] = useState<FleetFile | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [membersLoaded, setMembersLoaded] = useState(false);

	const { data: groupDetail, isLoading: isLoadingDetail } = useQuery({
		queryKey: ['getCustomerGroupDetail', groupId],
		queryFn: () => getCustomerGroupDetail(groupId),
		enabled: !!groupId,
		staleTime: 0,
	});

	// Populate form + tree file once detail is loaded
	useEffect(() => {
		if (!groupDetail?.data) return;
		const { name, address, startDate, companyTreeFile } = groupDetail.data;
		form.setFieldsValue({
			groupName: name,
			groupAddress: address,
			startDate: startDate ? dayjs(startDate) : undefined,
		});
		if (companyTreeFile) {
			setTreeFile({
				id: String(companyTreeFile.id),
				name: companyTreeFile.name,
				path: companyTreeFile.path ?? '',
				url: companyTreeFile.url,
			});
		}
	}, [groupDetail, form]);

	// Fetch member details via get-customer-members using locals, corporates,
	// and retails. Estar members are NOT sent through get-customer-members —
	// their exposure is already snapshotted on the group detail payload.
	const locals = groupDetail?.data?.customerList?.locals ?? [];
	const corporates = groupDetail?.data?.customerList?.corporates ?? [];
	const retails = groupDetail?.data?.customerList?.retails ?? [];
	const nonEstarEntries = [
		...locals.map((c) => ({
			customerId: c.customerId!,
			relationshipId: c.relationshipId ?? null,
			memberType: 'local' as MemberType,
		})),
		...corporates.map((c) => ({
			customerId: c.customerId!,
			relationshipId: (c.relationshipId as number | null) ?? null,
			memberType: 'corporate' as MemberType,
		})),
		...retails.map((c) => ({
			customerId: c.customerId!,
			relationshipId: (c.relationshipId as number | null) ?? null,
			memberType: 'retail' as MemberType,
		})),
	];
	const nonEstarCustomerIds = nonEstarEntries
		.map((m) => m.customerId)
		.filter(Boolean);
	const { data: membersData, isLoading: isLoadingMembers } = useQuery({
		queryKey: [
			'getCustomerGroupMembers',
			groupId,
			nonEstarCustomerIds.join(','),
		],
		queryFn: async () => {
			const memberRequest: CustomerGroupMember[] = nonEstarEntries.map((m) => ({
				customerId: m.customerId,
				relationshipId: m.relationshipId,
				memberType: m.memberType,
			}));
			const res = await getCustomerMembers(memberRequest);
			const memberTypeMap = new Map(
				nonEstarEntries.map((e) => [e.customerId, e.memberType]),
			);
			return (res.data ?? []).map((m) => ({
				...m,
				memberType:
					memberTypeMap.get(m.customerId ?? '') ?? ('local' as MemberType),
			}));
		},
		enabled: nonEstarCustomerIds.length > 0 && !membersLoaded,
		staleTime: 0,
	});

	useEffect(() => {
		if (membersLoaded) return;
		if (!groupDetail?.data) return;
		// If there are non-estar members we must wait for get-customer-members
		// to finish before merging. Pure-estar groups skip that call entirely.
		if (nonEstarCustomerIds.length > 0 && !membersData) return;

		const nonEstarMembers: EditableMember[] = (membersData ?? []).map((m) => ({
			...m,
			relationshipId: (m.relationship?.id as number | null) ?? null,
			memberType: m.memberType ?? ('local' as MemberType),
		}));

		// Estar members carry only identity + relationship info on the member
		// table. Exposure is N/A (no JF/NJF split) and will show as `-`.
		const estarMembers: EditableMember[] =
			(groupDetail.data.customerList?.estars ?? []).map((e) => ({
				customerId: e.customerId,
				customerName: e.customerName,
				npwpNo: e.npwpNo,
				relationship:
					e.relationshipId != null
						? {
							id: e.relationshipId,
							value: e.relationshipDescription ?? '',
						}
						: null,
				relationshipId: (e.relationshipId as number | null) ?? null,
				memberType: 'estar' as MemberType,
			}));

		setMembers([...nonEstarMembers, ...estarMembers]);
		setMembersLoaded(true);
	}, [membersData, groupDetail, membersLoaded, nonEstarCustomerIds.length]);

	const updateMutation = useMutation({
		mutationFn: updateCustomerGroup,
		onSuccess: () => {
			message.success('Group BMPP updated successfully');
			queryClient.invalidateQueries({ queryKey: ['getCustomerGroups'] });
			queryClient.invalidateQueries({ queryKey: ['getCustomerGroupDetail'] });
			queryClient.invalidateQueries({ queryKey: ['getCustomerGroupMembers'] });
			navigate(returnPage);
		},
		onError: (error: unknown) => {
			const errMsg = (
				error as { response?: { data?: { message?: string } } }
			)?.response?.data?.message;
			message.error(errMsg || 'Failed to update Group BMPP');
		},
	});

	const handleSaveClick = async () => {
		try {
			await form.validateFields();
		} catch {
			return;
		}
		if (members.length === 0) {
			message.error(
				'Mohon Member of Group Customer BMPP dipilih terlebih dahulu',
			);
			return;
		}
		if (members.some((m) => m.relationshipId == null)) {
			message.error(
				'Mohon Kelompok BMPP untuk semua member dilengkapi terlebih dahulu',
			);
			return;
		}
		if (!treeFile) {
			message.error('Please upload Company Tree');
			return;
		}
		setShowConfirm(true);
	};

	const handleConfirmYa = () => {
		const values = form.getFieldsValue() as FormValues;
		setShowConfirm(false);
		updateMutation.mutate({
			id: groupId,
			name: values.groupName,
			address: values.groupAddress,
			startDate: values.startDate
				? values.startDate.format('YYYY-MM-DD')
				: '',
			isActive: true,
			notes: values.notes,
			companyTreeFile: {
				id: String(treeFile!.id),
				name: treeFile!.name,
				path: treeFile!.path ?? '',
			},
			members: members.map((m) => ({
				customerId: m.customerId!,
				relationshipId: m.relationshipId,
				memberType: m.memberType,
			})),
		});
	};

	const handleSaveEditMember = () => {
		if (editingMemberIndex === null || editRelationshipId == null) return;
		setMembers((prev) =>
			prev.map((m, i) =>
				i === editingMemberIndex ? { ...m, relationshipId: editRelationshipId } : m,
			),
		);
		setEditingMemberIndex(null);
		setEditRelationshipId(undefined);
	};

	const noteHistory: CustomerGroupNoteHistory[] =
		groupDetail?.data?.notesHistory ?? [];

	const memberColumns: TableColumnType<EditableMember>[] = [
		{
			title: 'Action',
			align: 'center',
			render: (_, __, index) => (
				<Space split={<Divider type="vertical" style={{ margin: 0 }} />}>
					<EditOutlined
						style={{ color: '#1557BC', cursor: 'pointer' }}
						onClick={() => {
							setEditingMemberIndex(index);
							setEditRelationshipId(members[index].relationshipId);
						}}
					/>
					<DeleteOutlined
						style={{ color: '#1557BC', cursor: 'pointer' }}
						onClick={() => {
							setDeletingMemberIndex(index);
						}}
					/>
				</Space>
			),
		},
		{
			title: 'Customer ID',
			dataIndex: 'customerId',
		},
		{
			title: 'Nama Customer',
			render: (_, record) =>
				record.customerId ? (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							openCustomerDetail(String(record.customerId));
						}}
					>
						{record.customerName}
					</a>
				) : (
					record.customerName ?? '-'
				),
		},
		{
			title: 'NPWP',
			render: (_, record) =>
				record.npwpNo ? formatNPWPValue(record.npwpNo) : '-',
		},
		{
			title: 'Kelompok BMPP',
			dataIndex: 'relationshipId',
			onCell: (record) => ({
				style: record.relationshipId == null ? { backgroundColor: '#fff1f0' } : undefined,
			}),
			render: (val: number | null) =>
				val != null ? (RELATIONSHIP_LABELS[val] ?? val) : '-',
		},
		{
			title: 'Limit Pembiayaan JF',
			align: 'right',
			render: (_, record) =>
				record.jfFinancingLimitAmount
					? formatCurrency(record.jfFinancingLimitAmount)
					: '-',
		},
		{
			title: 'Limit Pembiayaan NJF',
			align: 'right',
			render: (_, record) =>
				record.njfFinancingLimitAmount
					? formatCurrency(record.njfFinancingLimitAmount)
					: '-',
		},
		{
			title: 'NTF JF',
			align: 'right',
			render: (_, record) =>
				record.jfNtfAmount
					? formatCurrency(record.jfNtfAmount)
					: '-',
		},
		{
			title: 'NTF NJF',
			align: 'right',
			render: (_, record) =>
				record.njfNtfAmount
					? formatCurrency(record.njfNtfAmount)
					: '-',
		},
		{
			title: 'OS Principal JF',
			align: 'right',
			render: (_, record) =>
				record.jfOutstandingPrincipalAmount
					? formatCurrency(record.jfOutstandingPrincipalAmount)
					: '-',
		},
		{
			title: 'OS Principal NJF',
			align: 'right',
			render: (_, record) =>
				record.njfOutstandingPrincipalAmount
					? formatCurrency(record.njfOutstandingPrincipalAmount)
					: '-',
		},
		{
			title: 'Sisa Limit JF',
			align: 'right',
			render: (_, record) =>
				record.jfRemainingLimitAmount
					? formatCurrency(record.jfRemainingLimitAmount)
					: '-',
		},
		{
			title: 'Sisa Limit NJF',
			align: 'right',
			render: (_, record) =>
				record.njfRemainingLimitAmount
					? formatCurrency(record.njfRemainingLimitAmount)
					: '-',
		},
		{
			title: 'Sumber',
			align: 'center',
			render: (_, record) => {
				const typeKey = (record.memberType ?? '').toString().toLowerCase();
				const typeShort = MEMBER_TYPE_SHORT[typeKey];
				const typeColor = MEMBER_TYPE_COLOR[typeKey];
				if (!typeShort) return '-';
				return (
					<span style={{ fontWeight: 700, color: typeColor }}>
						{typeShort}
					</span>
				);
			},
		},
	];

	const noteHistoryColumns: TableColumnType<CustomerGroupNoteHistory & { key: number }>[] = [
		{
			title: 'No',
			dataIndex: 'key',
			align: 'center',
			width: 60,
		},
		{
			title: 'Name',
			dataIndex: 'username',
		},
		{
			title: 'Date',
			dataIndex: 'date',
			render: (val: string) =>
				val ? dayjs(val).format('DD/MM/YYYY') : '-',
		},
		{
			title: 'Status',
			dataIndex: 'status',
		},
		{
			title: 'Approval Notes',
			dataIndex: 'notes',
		},
	];

	const isLoading = isLoadingDetail || isLoadingMembers;

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size="large"
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
				<Button
					onClick={() => navigate(returnPage)}
					icon={<ArrowLeftOutlined />}
					style={{ width: 44, height: 44, background: '#1557BC', color: '#fff' }}
				/>
				<FleetTitle marginBottom={0} style={{ flex: 1 }}>Edit Group Customer BMPP</FleetTitle>
			</div>
			<div>
				<Spin spinning={isLoading}>
					<Form
						{...ANTD_DEFAULT_PROPS.FORM}
						form={form}
					>
						<Row gutter={[20, 20]}>
							<Col span={12}>
								<Form.Item
									label="Group Name"
									name="groupName"
									rules={[{ required: true, message: 'Please input Group Name' }]}
									labelCol={{ span: 8 }}
									wrapperCol={{ span: 16 }}
								>
									<Input placeholder="Input Group Name" onChange={(e) => form.setFieldValue('groupName', e.target.value.toUpperCase())} />
								</Form.Item>
							</Col>
						</Row>

						<Divider />

						<Row gutter={[20, 20]}>
							<Col span={24}>
								<Form.Item
									label="Group Address"
									name="groupAddress"
									rules={[
										{ required: true, message: 'Please input Group Address' },
									]}
									labelCol={{ span: 4 }}
									wrapperCol={{ span: 20 }}
								>
									<Input placeholder="Input Group Address" />
								</Form.Item>
							</Col>
						</Row>

						<Divider />

						<Row gutter={[20, 20]}>
							<Col span={12}>
								<Form.Item
									label="Start Date"
									name="startDate"
									rules={[{ required: true, message: 'Please select Start Date' }]}
									labelCol={{ span: 8 }}
									wrapperCol={{ span: 16 }}
								>
									<DatePicker
										format="DD/MM/YYYY"
										style={{ width: '100%' }}
										disabledDate={(current) =>
											current && current > dayjs().endOf('day')
										}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Divider />

						<Flex
							justify="flex-end"
							style={{ marginBottom: 16 }}
						>
							<Button
								style={{
									color: '#1557BC',
									borderRadius: 50,
									fontSize: 12,
									height: 24,
									borderColor: '#1557BC',
									fontWeight: 700,
								}}
								onClick={() => setIsAddModalOpen(true)}
							>
								+ Add Member
							</Button>
						</Flex>

						<Table
							bordered
							scroll={{ x: 'max-content' }}
							pagination={false}
							loading={isLoadingMembers}
							dataSource={members.map((m, key) => ({ ...m, key }))}
							columns={memberColumns}
							style={{ marginBottom: 24 }}
						/>
						{members.some((m) => m.relationshipId == null) && (
							<Alert
								type="error"
								showIcon
								message="Terdapat member yang belum memiliki Kelompok BMPP. Mohon lengkapi dengan menekan tombol Edit pada member terkait."
								style={{ marginTop: 20, marginBottom: 24 }}
							/>
						)}

						<Divider />

						<Row gutter={[20, 20]}>
							<Col span={12}>
								<Form.Item
									label="Upload Company Tree"
									required
									style={{ marginBottom: 24 }}
									labelCol={{ span: 8 }}
									wrapperCol={{ span: 16 }}
								>
									<Upload.Dragger
										listType="picture"
										accept=".pdf,.jpg,.jpeg,.png"
										maxCount={1}
										fileList={
											treeFile
												? ([
													{
														uid: String(treeFile.id),
														name: treeFile.name,
														status: 'done',
														url: treeFile.url,
													},
												] as UploadFile[])
												: []
										}
										beforeUpload={async (file) => {
											setIsUploading(true);
											try {
												const fileid = Date.now().toString();
												const filepath = `customer-group/tree`;
												await uploader({ file, fileid, filepath });
												setTreeFile({
													id: fileid,
													name: file.name,
													path: filepath,
													url: await blobToBase64(file),
												});
											} catch {
												message.error('Failed to upload file');
											} finally {
												setIsUploading(false);
											}
											return false;
										}}
										onRemove={() => setTreeFile(null)}
									>
										{isUploading ? (
											<LoadingOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										) : (
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										)}
									</Upload.Dragger>
								</Form.Item>
							</Col>
						</Row>

						{noteHistory.length > 0 && (
							<>
								<Divider />
								<FleetTitle>Note History</FleetTitle>
								<Table
									bordered
									pagination={false}
									dataSource={noteHistory.map((item, index) => ({
										...item,
										key: index + 1,
									}))}
									columns={noteHistoryColumns}
									style={{ marginBottom: 24 }}
								/>
							</>
						)}

						<Divider />

						<Row gutter={[20, 20]}>
							<Col span={24}>
								<Form.Item
									label="Informasi Tambahan"
									name="notes"
									labelCol={{ span: 4 }}
									wrapperCol={{ span: 20 }}
									style={{ marginBottom: 40 }}
								>
									<Input.TextArea
										rows={4}
										placeholder="Input informasi tambahan"
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row justify="end">
							<Col>
								<Button
									type="primary"
									onClick={handleSaveClick}
									loading={updateMutation.isPending}
									disabled={members.some((m) => m.relationshipId == null)}
								>
									SAVE
								</Button>
							</Col>
						</Row>
					</Form>
				</Spin>
			</div>

			<CustomerGroupAddMemberModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				existingCustomerIds={members.map((m) => m.customerId ?? '').filter(Boolean)}
				onSelect={(member) => {
					if (!members.find((m) => m.customerId === member.customerId)) {
						setMembers((prev) => [
							...prev,
							{
								customerId: member.customerId ?? '',
								customerName: member.customerName ?? '',
								npwpNo: member.npwpNo ?? '',
								relationshipId: member.relationshipId ?? BmppGroupRelationship.Parent,
								memberType: (member.memberType as MemberType) ?? 'local',
								jfFinancingLimitAmount: member.jfFinancingLimitAmount,
								njfFinancingLimitAmount: member.njfFinancingLimitAmount,
								jfNtfAmount: member.jfNtfAmount,
								njfNtfAmount: member.njfNtfAmount,
								jfOutstandingPrincipalAmount: member.jfOutstandingPrincipalAmount,
								njfOutstandingPrincipalAmount: member.njfOutstandingPrincipalAmount,
								jfRemainingLimitAmount: member.jfRemainingLimitAmount,
								njfRemainingLimitAmount: member.njfRemainingLimitAmount,
							},
						]);
					} else {
						message.warning('Customer is already added to the list.');
					}
				}}
			/>

			<Modal
				title={<FleetTitle level={2}>Edit Member of Group Customer BMPP</FleetTitle>}
				open={editingMemberIndex !== null}
				onCancel={() => {
					setEditingMemberIndex(null);
					setEditRelationshipId(undefined);
				}}
				width={960}
				footer={[
					<Button
						key="back"
						onClick={() => {
							setEditingMemberIndex(null);
							setEditRelationshipId(undefined);
						}}
					>
						BACK
					</Button>,
					<Button
						key="submit"
						type="primary"
						onClick={handleSaveEditMember}
						disabled={editRelationshipId == null}
					>
						SAVE
					</Button>,
				]}
			>
				{editingMemberIndex !== null && (
					<>
						<Divider />
						<Form {...ANTD_DEFAULT_PROPS.FORM}>
							<Row gutter={[20, 20]}>
								<Col span={12}>
									<Form.Item
										label="Customer Name"
										labelCol={{ span: 8 }}
										wrapperCol={{ span: 16 }}
										required
									>
										<Input
											readOnly
											disabled
											value={members[editingMemberIndex]?.customerName}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Kelompok BMPP"
										labelCol={{ span: 8 }}
										wrapperCol={{ span: 16 }}
										required
									>
										<Select
											value={editRelationshipId}
											onChange={setEditRelationshipId}
											placeholder="Select Kelompok BMPP"
										>
											<Select.Option value={BmppGroupRelationship.Parent}>
												Pihak Terkait
											</Select.Option>
											<Select.Option value={BmppGroupRelationship.Subsidiary}>
												Bukan Pihak Terkait
											</Select.Option>
											<Select.Option value={BmppGroupRelationship.Affiliate}>
												Kelompok Bukan Pihak Terkait
											</Select.Option>
										</Select>
									</Form.Item>
								</Col>
							</Row>

							<Divider />

							<Row gutter={[20, 20]} style={{ marginBottom: 40 }}>
								<Col span={12}>
									<Form.Item
										label="Customer ID"
										labelCol={{ span: 8 }}
										wrapperCol={{ span: 16 }}
									>
										<Input
											readOnly
											variant="borderless"
											style={{ padding: 0 }}
											value={members[editingMemberIndex]?.customerId}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="NPWP"
										labelCol={{ span: 8 }}
										wrapperCol={{ span: 16 }}
									>
										<Input
											readOnly
											variant="borderless"
											style={{ padding: 0 }}
											value={members[editingMemberIndex]?.npwpNo ? formatNPWPValue(members[editingMemberIndex].npwpNo!) : ''}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</>
				)}
			</Modal>

		<Modal
			open={deletingMemberIndex !== null}
			footer={null}
			onCancel={() => setDeletingMemberIndex(null)}
			centered
			width={400}
		>
			<div style={{ textAlign: 'center', padding: '24px 0' }}>
				<InfoCircleFilled
					style={{ fontSize: 64, color: '#1557BC', marginBottom: 16 }}
				/>
				<Typography.Text
					style={{
						display: 'block',
						fontSize: 16,
						fontWeight: 500,
						marginBottom: 32,
					}}
				>
					Apakah Anda yakin akan menghapus Customer berikut dari Member of Group Customer BMPP?
				</Typography.Text>
				<Space size={16}>
					<Button
						style={{ minWidth: 120 }}
						onClick={() => setDeletingMemberIndex(null)}
					>
						TIDAK
					</Button>
					<Button
						type="primary"
						style={{ minWidth: 120 }}
						onClick={() => {
							setMembers((prev) =>
								prev.filter((_, i) => i !== deletingMemberIndex),
							);
							setDeletingMemberIndex(null);
						}}
					>
						YA
					</Button>
				</Space>
			</div>
		</Modal>

		<Modal
			open={showConfirm}
			footer={null}
			onCancel={() => setShowConfirm(false)}
			centered
			width={400}
		>
			<div style={{ textAlign: 'center', padding: '24px 0' }}>
				<InfoCircleFilled
					style={{ fontSize: 64, color: '#1557BC', marginBottom: 16 }}
				/>
				<Typography.Text
					style={{
						display: 'block',
						fontSize: 16,
						fontWeight: 500,
						marginBottom: 32,
					}}
				>
					Apakah Anda yakin data yang diinputkan sudah benar?
				</Typography.Text>
				<Space size={16}>
					<Button
						style={{ minWidth: 120 }}
						onClick={() => setShowConfirm(false)}
					>
						TIDAK
					</Button>
					<Button
						type="primary"
						style={{ minWidth: 120 }}
						loading={updateMutation.isPending}
						onClick={handleConfirmYa}
					>
						YA
					</Button>
				</Space>
			</div>
		</Modal>
		</Space>
	);
};

export default EditCustomerGroupPage;
